import { useActiveSection } from "@/hooks/useReveal";
import { useEffect, useState } from "react";

const PALETTE = {
  ink: "#1a1a1a",
  muted: "#5a5249",
  amber: "#C4823A",
  card: "#FFFFFF",
  line: "rgba(0,0,0,0.08)",
};

type RailSection = { id: string; n: string; label: string };

const SECTIONS: RailSection[] = [
  { id: "capability", n: "01", label: "What she does" },
  { id: "compare",    n: "02", label: "Eva vs." },
  { id: "proof",      n: "03", label: "Proof" },
  { id: "pricing",    n: "04", label: "Pricing" },
  { id: "faq",        n: "05", label: "FAQ" },
];

export const SectionRail = ({ sections = SECTIONS }: { sections?: RailSection[] }) => {
  const active = useActiveSection(sections.map(s => s.id));
  const [show, setShow] = useState(false);
  const [wide, setWide] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1180px)");
    setWide(mq.matches);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  if (!wide) return null;

  return (
    <div
      aria-hidden={!show}
      style={{
        position: "fixed",
        left: 22,
        top: "50%",
        transform: `translateY(-50%) ${show ? "translateX(0)" : "translateX(-12px)"}`,
        opacity: show ? 1 : 0,
        transition: "opacity 280ms ease, transform 280ms cubic-bezier(.2,.8,.2,1)",
        pointerEvents: show ? "auto" : "none",
        zIndex: 30,
        display: "flex", flexDirection: "column", gap: 18,
      }}
    >
      {sections.map((s) => {
        const isActive = active === s.id;
        return (
          <a
            key={s.id}
            href={`#${s.id}`}
            aria-label={`Jump to ${s.label}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 12,
              textDecoration: "none", color: PALETTE.muted,
              position: "relative",
            }}
            className="rail-dot"
          >
            <span
              data-rail-active={isActive ? "true" : "false"}
              style={{
                width: isActive ? 12 : 6, height: isActive ? 12 : 6,
                borderRadius: "50%",
                background: isActive ? PALETTE.amber : "rgba(0,0,0,0.18)",
                boxShadow: isActive ? `0 0 12px ${PALETTE.amber}88` : "none",
                transition: "all 280ms cubic-bezier(.2,.8,.2,1)",
                flexShrink: 0,
              }}
            />
            <span
              className="rail-label"
              style={{
                fontSize: 11, fontFamily: "Barlow, sans-serif",
                color: isActive ? PALETTE.ink : PALETTE.muted,
                background: PALETTE.card,
                border: `1px solid ${PALETTE.line}`,
                padding: "5px 10px", borderRadius: 999,
                whiteSpace: "nowrap",
                opacity: 0, transform: "translateX(-6px)",
                transition: "opacity 200ms ease, transform 200ms ease",
                pointerEvents: "none",
              }}
            >
              <span style={{ color: PALETTE.amber, fontFamily: "'JetBrains Mono', monospace", marginRight: 6, fontWeight: 600 }}>{s.n}</span>
              {s.label}
            </span>
          </a>
        );
      })}
      <style>{`
        .rail-dot:hover .rail-label { opacity: 1 !important; transform: translateX(0) !important; }
        .rail-dot:focus-visible .rail-label { opacity: 1 !important; transform: translateX(0) !important; outline: 2px solid ${PALETTE.amber}; outline-offset: 2px; }
      `}</style>
    </div>
  );
};

export default SectionRail;